import React, { useMemo } from 'react'
import { Box, Paper, Stack } from '@mui/material'
import config from 'config'
import { useTranslation } from 'react-i18next'
import { camelCase } from 'lodash'

import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import PageLoading from 'components/atoms/PageLoading'

import useEntityData from 'hooks/useEntityData'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import { useMappedActions } from 'hooks/useMappedActions'

import PartnerDocumentsItem from './PartnerDocumentsItem'
import { useMapPartnerDocumentsActions } from './useMapPartnerDocumentsActions'
import usePartnerDocuments, {
  PARTNER_DOCUMENTS_TYPES,
  PARTNER_DOCUMENTS_TYPES_CAMEL_CASE,
} from './usePartnerDocuments'

const enrichPartnerByDocuments = (partner) => {
  const enrichedPartner = { ...partner }

  PARTNER_DOCUMENTS_TYPES_CAMEL_CASE.forEach((type) => {
    if (!enrichedPartner[`${type}AddedAt`]) {
      enrichedPartner[`${type}Documents`] = null
      return
    }

    const documents = Array.from({ length: 3 }, (_, i) => i + 1).map((i) => {
      const documentName = enrichedPartner[`${type}Document${i}`]
      return documentName
        ? `${config.apiBaseUrl}partners/sign-download-document?filename=${partner.id}/${documentName}`
        : null
    })

    enrichedPartner[`${type}Documents`] = documents
  })

  return enrichedPartner
}

export default function PartnerDocuments() {
  const { t } = useTranslation()

  const {
    others: partner,
    isFetching,
    refetch,
  } = useEntityData({
    entity: 'partners/partner',
  })

  const { isUpdating, addDocuments, removeDocuments } = usePartnerDocuments()
  const { mappedActions } = useMapPartnerDocumentsActions()
  const {
    setInLineError,
    getActionAdditionalParameters,
    getFormError,
    resetForm,
    getConfirmationFormFields,
  } = useMappedActions(mappedActions)

  const [selectedType, setSelectedType] = React.useState(null)

  const enrichedPartner = useMemo(
    () => enrichPartnerByDocuments(partner),
    [partner]
  )

  const handleConfirmAction = async () => {
    const stateChangeError = getFormError(selectedType, partner)

    if (stateChangeError) {
      setInLineError(stateChangeError)
      return false
    }

    const additionalParams = await getActionAdditionalParameters(selectedType)

    await addDocuments({
      type: selectedType,
      ...additionalParams,
    })

    refetch()
  }

  const {
    loading: confirmLoading,
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleConfirmAction)

  const readOnly = useMemo(
    () => confirmLoading || isUpdating,
    [confirmLoading, isUpdating]
  )

  const confirmationFormFields = useMemo(() => {
    return getConfirmationFormFields(selectedType, readOnly, partner)
  }, [getConfirmationFormFields, partner, readOnly, selectedType])

  const onOpenDialog = (type) => {
    setSelectedType(type)
    resetForm()

    handleOpenConfirmationDialog()
  }

  const handleCloseDialog = () => {
    handleCloseConfirmationDialog()
    setSelectedType(null)
  }

  const onDeleteDocuments = async (type) => {
    await removeDocuments(type)
    refetch()
  }

  return isFetching ? (
    <PageLoading />
  ) : (
    <>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          {PARTNER_DOCUMENTS_TYPES.map((typeOriginal, index) => {
            const type = camelCase(typeOriginal)

            return (
              <PartnerDocumentsItem
                key={type}
                index={index + 1}
                type={typeOriginal}
                documents={enrichedPartner[`${type}Documents`]}
                onButtonClick={onOpenDialog}
                onDelete={onDeleteDocuments}
                readOnly={readOnly}
              />
            )
          })}
        </Stack>
      </Paper>
      <ConfirmationDialog
        open={openConfirmationDialog}
        title={
          selectedType &&
          t(`partnerDocuments.types.${selectedType}.uploadDialog.title`)
        }
        handleClose={handleCloseDialog}
        handleSubmit={handleConfirmConfirmationDialog}
        contentComponent={Box}
        content={<>{confirmationFormFields}</>}
        readOnly={readOnly}
      />
    </>
  )
}
